import { APPLY_COUPON, SubscriptionSvc } from '../../services/SubscriptionSvc';
import { SubscriptionsParams } from '../../directives/Subscriptions';
import { Utils } from '../../../underscore-extensions';
import { SubscriptionMessagesSvc } from '../messages/SubscriptionMessagesSvc';
import { SubscriptionCouponMessagesSvc } from '../messages/SubscriptionCouponMessagesSvc';

export const PREDICT_PRICE_ERROR = 'website/subscription/predictPriceError';

class SubscriptionPredictPriceContainer implements ng.IComponentController {
    currentPrice: number;
    currentPeriod: string;
    period: string;
    planName: string;
    annualDiscount: number;
    percentsDiscount: number;
    amountDiscount: number;
    totalPrice: number;
    credit: number;
    loading: boolean;
    hide: boolean;
    private applyCouponListener: Function = _.noop;
    private staticSubscription: anm.SubscriptionDetails;
    private predictSubscription: SubscriptionPredict.Details;

    constructor(
        private $q: ng.IQService,
        private $scope: ng.IScope,
        private $state: ng.ui.IStateService,
        private subscriptionMessageSvc: SubscriptionMessagesSvc,
        private subscriptionCouponMessageSvc: SubscriptionCouponMessagesSvc,
        private $stateParams: SubscriptionsParams,
        private $rootScope: ng.IRootScopeService,
        private subscriptionSvc: SubscriptionSvc
    ) {
        this.setCoupon();

        this.applyCouponListener = this.$rootScope.$on(
            APPLY_COUPON,
            (e, coupon: string) => this.init(coupon)
        );
    }

    $onDestroy() {
        this.applyCouponListener();
    }

    $onChanges(onChangesObj: ng.IOnChangesObject) {
        if (!onChangesObj.period || onChangesObj.period.isFirstChange()) return;
        this.$rootScope.$emit(PREDICT_PRICE_ERROR, ''); // clear previous error message
        this.currentPeriod = onChangesObj.period.currentValue.toLowerCase();

        this.$state
            .go(
                this.$state.current,
                {
                    ...this.$stateParams,
                    period: this.currentPeriod
                },
                { notify: false }
            )
            .finally(() => !this.loading && this.setPrice());
    }

    init(coupon?: string) {
        this.setCurrentSubscription(coupon).then(() => this.setPrice());
    }

    setCoupon() {
        if (this.hide) return;

        if (this.$stateParams.coupon) {
            this.init(this.$stateParams.coupon);
        }
    }

    private setCurrentSubscription(coupon?: string) {
        this.loading = true;

        return this.$q
            .all([this.getPredictPrice(coupon), this.getStaticSubscription()])
            .then(this.handlerHttpResponse)
            .then(subscriptions => {
                const [predictSubscription, staticSubscription] = subscriptions;

                this.predictSubscription = predictSubscription;
                this.staticSubscription = staticSubscription;
            })
            .catch((errors: any[]) => {
                this.subscriptionMessageSvc.hideMassage();
                this.$rootScope.$emit(PREDICT_PRICE_ERROR, errors[0]);
                return this.$q.reject(errors);
            })
            .finally(() => {
                this.loading = false;
            });
    }

    private setPrice() {
        const { period, plan } = this.$stateParams;
        if (!plan) return;

        this.currentPeriod = this.currentPeriod || period;

        const {
            annualPrice,
            monthlyPrice,
            displayName
        } = this.staticSubscription;

        this.planName = displayName;

        // transform prices from array to object
        const pricesObj: {
            annual: SubscriptionPredict.Price;
            monthly: SubscriptionPredict.Price;
        } = this.predictSubscription.prices.reduce(
            (prices: any, price: SubscriptionPredict.Price) => {
                if (price.period === 'ANNUAL') {
                    prices.annual = price;
                } else if (price.period === 'MONTHLY') {
                    prices.monthly = price;
                }
                return prices;
            },
            {}
        );

        let currentPredictPriceData: SubscriptionPredict.Price;

        if (this.currentPeriod === 'annual') {
            currentPredictPriceData = pricesObj.annual;
            this.currentPrice = (monthlyPrice * 12) / 100 || annualPrice / 100;
            this.annualDiscount = this.currentPrice - annualPrice / 100;
        } else if (this.currentPeriod === 'monthly') {
            currentPredictPriceData = pricesObj.monthly;
            this.currentPrice = monthlyPrice / 100;
            this.annualDiscount = null;
        } else {
            throw new Error('incorrect period name');
        }

        this.setupDiscount(currentPredictPriceData);

        const currentPredictPrice = currentPredictPriceData.price / 100;
        const currentPredictPriceRounded = this.subscriptionSvc.round(
            currentPredictPrice
        );

        const startingBalance = this.subscriptionSvc.round(
            this.predictSubscription.startingBalance / 100
        );
        this.credit = startingBalance < 0 ? -startingBalance : 0;
        this.totalPrice =
            currentPredictPriceRounded >= 0 ? currentPredictPriceRounded : 0;
        this.subscriptionSvc.totalPriceLastValue = this.totalPrice;
    }

    private setupDiscount({ discount }: SubscriptionPredict.Price) {
        this.subscriptionMessageSvc.hideMassage();

        if (!discount) return;

        const { error, amount, percents } = discount;

        if (error) {
            this.$rootScope.$emit(
                PREDICT_PRICE_ERROR,
                this.subscriptionSvc.getError(error)
            );
        } else {
            this.amountDiscount = amount / 100;
            this.percentsDiscount = percents;
            const message = this.subscriptionCouponMessageSvc.get(discount);
            this.subscriptionMessageSvc.showSuccess(message);
        }
    }

    private handlerHttpResponse = (
        responses: (any | ApiError)[]
    ): ng.IPromise<[SubscriptionPredict.Details, anm.SubscriptionDetails]> => {
        const deferred = this.$q.defer();
        const errors: string[] = [];

        responses.forEach(res => {
            if (Utils.isApiError(res)) {
                errors.push(this.subscriptionSvc.getError(res.message));
            }
        });

        if (errors.length > 0) {
            deferred.reject(errors);
        } else {
            deferred.resolve(responses);
        }

        return deferred.promise;
    }

    private getStaticSubscription() {
        return this.subscriptionSvc.findSubscriptionByName(
            this.$stateParams.plan
        );
    }

    private getPredictPrice(coupon?: string) {
        const { plan, period } = this.$stateParams;
        return this.subscriptionSvc.getPredictPrice({
            period,
            coupon,
            displayName: plan as anm.SubscriptionDisplayName
        });
    }
}

angular
    .module('animatronApp.SubscriptionPredictPriceContainer', [])
    .component('subscriptionPredictPriceContainer', {
        bindings: {
            period: '<',
            hide: '<'
        },
        controller: SubscriptionPredictPriceContainer,
        templateUrl: require('settings/subscription/containers/subscription-predict-price-container.html')
    });



// WEBPACK FOOTER //
// ./app/assets/javascripts/settings/subscription/containers/SubscriptionPredictPriceContainer.ts